import React from 'react';
import { useTranslation } from 'react-i18next';

import { Field, Button, Icon, Sheet, useSheetDismiss } from '@/nova/components';
import type { LandingDestination } from '@/nova/pages/creatives/hooks/useLandingDestinations';
import { createCampaign, getAffiliateError, setCampaignStatus, updateCampaign } from '../../api';
import type { AffiliateCampaign } from '../../types';
import { isCampaignActive } from '../../mapping';
import {
  endBeforeStart,
  landingOptions,
  localYmd,
  selectionFor,
  websiteOption,
  type DestinationOption,
} from '../../destinations';
import { OpenUrlButton } from '../OpenUrlButton/OpenUrlButton';
import { DestinationPicker } from '../DestinationPicker/DestinationPicker';
import {
  FormCol,
  GroupLabel,
  StatusRow,
  SegChips,
  SegChip,
  DateRow,
  DateLabel,
  DateLabelText,
  DateInput,
  ErrorText,
  MoreToggle,
} from '../formControls.styles';

export type CampaignFormProps = {
  casinoInstanceId: string;
  campaign?: AffiliateCampaign;
  /** The brand homepage (`https://<domain>`), '' when the brand has no website address. */
  brandBaseUrl: string;
  /** The brand's live landing pages (read once by the page, shared with the campaign cards). */
  landingDestinations: LandingDestination[];
  landingLoading: boolean;
  onClose: () => void;
  onSaved: () => void;
  onToast: (message: string) => void;
};

const trimOrUndefined = (value: string): string | undefined => {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

/** A stored campaign date as a date input's `YYYY-MM-DD` ('' when it is not one). */
const asYmd = (value: string | undefined): string => {
  const match = /^\d{4}-\d{2}-\d{2}/.exec(value?.trim() ?? '');
  return match ? match[0] : '';
};

/**
 * Add / edit a campaign for the current brand. The backend pins the brand; we own name, destination, dates, reach
 * and status. The destination is picked from what the brand already has (its homepage, its live landing pages) or
 * typed as a custom URL; picking a named page also names an unnamed campaign. A new
 * campaign starts at the homepage and today's date. The rarely-needed fields sit under "More options".
 */
export const CampaignForm = ({
  casinoInstanceId,
  campaign,
  brandBaseUrl,
  landingDestinations,
  landingLoading,
  onClose,
  onSaved,
  onToast,
}: CampaignFormProps): React.ReactElement => {
  const { t } = useTranslation();
  const { closeHandle, dismiss } = useSheetDismiss(onClose);
  const isEdit = campaign !== undefined;

  const [name, setName] = React.useState(campaign?.name ?? '');
  // The name the picker last filled in: a later pick may replace it, never a name the owner typed.
  const [autoName, setAutoName] = React.useState<string | null>(null);
  const [landingUrl, setLandingUrl] = React.useState(campaign ? (campaign.landing_url ?? '') : brandBaseUrl);
  const [customChosen, setCustomChosen] = React.useState(false);
  const [overrideUrl, setOverrideUrl] = React.useState(campaign?.override_url ?? '');
  const [startDate, setStartDate] = React.useState(() => (campaign ? asYmd(campaign.start_date) : localYmd(new Date())));
  const [endDate, setEndDate] = React.useState(asYmd(campaign?.end_date));
  const [availableToAll, setAvailableToAll] = React.useState<boolean>(campaign?.available_to_all ?? true);
  const [externalRef, setExternalRef] = React.useState(campaign?.external_ref ?? '');
  const [moreOpen, setMoreOpen] = React.useState(
    () => !!campaign?.override_url || !!campaign?.external_ref || campaign?.available_to_all === false,
  );
  const [saving, setSaving] = React.useState(false);

  const [active, setActive] = React.useState<boolean>(campaign ? isCampaignActive(campaign) : true);
  const [statusSaving, setStatusSaving] = React.useState(false);

  const website = React.useMemo(() => websiteOption(brandBaseUrl), [brandBaseUrl]);
  const landing = React.useMemo(() => landingOptions(landingDestinations), [landingDestinations]);
  const selection = React.useMemo(() => {
    const options = [...(website ? [website] : []), ...landing];
    return selectionFor(landingUrl, customChosen, options, landingLoading);
  }, [website, landing, landingUrl, customChosen, landingLoading]);

  const onPick = (option: DestinationOption): void => {
    setCustomChosen(false);
    setLandingUrl(option.url);
    if (option.name && (name.trim() === '' || name === autoName)) {
      setName(option.name);
      setAutoName(option.name);
    } else if (!option.name && autoName !== null && name === autoName) {
      // An unnamed place (the homepage) after a named one: the previous page's name no longer fits.
      setName('');
      setAutoName(null);
    }
  };

  const datesConflict = endBeforeStart(startDate, endDate);
  const canSubmit = name.trim().length > 0 && !datesConflict && !saving;

  const onSubmit = React.useCallback(async () => {
    if (!canSubmit) return;
    setSaving(true);
    const input = {
      name: name.trim(),
      landing_url: trimOrUndefined(landingUrl),
      override_url: trimOrUndefined(overrideUrl),
      start_date: trimOrUndefined(startDate),
      end_date: trimOrUndefined(endDate),
      available_to_all: availableToAll,
      external_ref: trimOrUndefined(externalRef),
    };
    try {
      if (isEdit && campaign) {
        await updateCampaign(casinoInstanceId, campaign.id, input);
        onToast(t('Campaign updated'));
      } else {
        await createCampaign(casinoInstanceId, input);
        onToast(t('Campaign created'));
      }
      onSaved();
      dismiss();
    } catch (err: unknown) {
      onToast(getAffiliateError(err).message);
    } finally {
      setSaving(false);
    }
  }, [
    canSubmit,
    isEdit,
    campaign,
    casinoInstanceId,
    name,
    landingUrl,
    overrideUrl,
    startDate,
    endDate,
    availableToAll,
    externalRef,
    onToast,
    onSaved,
    dismiss,
    t,
  ]);

  const onSetStatus = React.useCallback(
    async (next: boolean) => {
      if (!campaign || statusSaving || next === active) return;
      setStatusSaving(true);
      const previous = active;
      setActive(next);
      try {
        await setCampaignStatus(casinoInstanceId, campaign.id, next ? 'active' : 'inactive');
        onToast(next ? t('Campaign activated') : t('Campaign paused'));
        onSaved();
      } catch (err: unknown) {
        setActive(previous);
        onToast(getAffiliateError(err).message);
      } finally {
        setStatusSaving(false);
      }
    },
    [campaign, statusSaving, active, casinoInstanceId, onToast, onSaved, t],
  );

  return (
    <Sheet
      title={isEdit ? t('Edit {{name}}') : t('Add campaign')}
      titlePlaceholders={isEdit ? { name: campaign?.name ?? '' } : undefined}
      ariaLabel={isEdit ? t('Edit campaign') : t('Add campaign')}
      onClose={onClose}
      closeHandle={closeHandle}
      busy={saving}
      animateResize
      footer={
        <Button
          label={isEdit ? t('Save changes') : t('Add campaign')}
          variant="primary"
          fullWidth
          onClick={onSubmit}
          disabled={!canSubmit}
        />
      }
    >
      <FormCol>
        <DestinationPicker
          website={website}
          landing={landing}
          landingLoading={landingLoading}
          value={landingUrl}
          selection={selection}
          onPick={onPick}
          onCustom={() => setCustomChosen(true)}
          onChangeCustom={(url: string) => {
            // Typing is a custom choice: without it, text that passes through an option's address (the homepage on
            // the way to a deeper URL) would re-select that option and fold the field mid-edit.
            setCustomChosen(true);
            setLandingUrl(url);
          }}
        />

        <Field label={t('Campaign name')} value={name} placeholder={t('Welcome offer')} onChange={setName} />

        <DateRow>
          <DateLabel>
            <DateLabelText text="Start date" tag="span" />
            <DateInput
              type="date"
              value={startDate}
              max={endDate || undefined}
              $invalid={datesConflict}
              onChange={(event) => setStartDate(event.target.value)}
            />
          </DateLabel>
          <DateLabel>
            <DateLabelText text="End date (optional)" tag="span" />
            <DateInput
              type="date"
              value={endDate}
              min={startDate || undefined}
              $invalid={datesConflict}
              onChange={(event) => setEndDate(event.target.value)}
            />
          </DateLabel>
        </DateRow>
        {datesConflict ? <ErrorText text="The end date is before the start date." tag="span" /> : null}

        {isEdit && campaign ? (
          <StatusRow>
            <GroupLabel text="Status" tag="span" />
            <SegChips>
              <SegChip type="button" $selected={active} aria-pressed={active} onClick={() => onSetStatus(true)}>
                {t('Active')}
              </SegChip>
              <SegChip type="button" $selected={!active} aria-pressed={!active} onClick={() => onSetStatus(false)}>
                {t('Paused')}
              </SegChip>
            </SegChips>
          </StatusRow>
        ) : null}

        <MoreToggle type="button" $open={moreOpen} aria-expanded={moreOpen} onClick={() => setMoreOpen((v) => !v)}>
          {t('More options')}
          <Icon name="chevron-down" size={14} />
        </MoreToggle>

        {moreOpen ? (
          <>
            <Field
              label={t('Override URL (optional)')}
              value={overrideUrl}
              placeholder="https://brand.com/alt"
              onChange={setOverrideUrl}
              action={<OpenUrlButton url={overrideUrl} />}
            />

            <StatusRow>
              <GroupLabel text="Open to all affiliates" tag="span" />
              <SegChips>
                <SegChip type="button" $selected={availableToAll} aria-pressed={availableToAll} onClick={() => setAvailableToAll(true)}>
                  {t('Yes')}
                </SegChip>
                <SegChip type="button" $selected={!availableToAll} aria-pressed={!availableToAll} onClick={() => setAvailableToAll(false)}>
                  {t('No')}
                </SegChip>
              </SegChips>
            </StatusRow>

            <Field label={t('External reference (optional)')} value={externalRef} placeholder={t('Your own id')} onChange={setExternalRef} />
          </>
        ) : null}
      </FormCol>
    </Sheet>
  );
};

export default CampaignForm;
